import { useQuery } from "@tanstack/react-query"
import { useAuth } from "../../../../hooks/useAuth"
import type { useCompanyApi } from "../../../../hooks/useCompanyApi"
import { fetchImageModels } from "../../../../lib/api"
import { queryKeys } from "../../../../lib/query-keys"
import { ImageGenerationPanelView } from "./ImageGenerationPanelView"

interface IProps {
  settings: Record<string, string>
  /** Secret keys reported as already-configured (masked) by the backend. */
  configuredSecrets: Set<string>
  saving: boolean
  /** Super-admin platform view — only they may set the platform Runware key. */
  isAdminOnly: boolean
  /** Owner/admin (or super-admin) may change the image model; a member is read-only. */
  canManage?: boolean
  company: ReturnType<typeof useCompanyApi>
  updateSetting: (key: string, value: string) => void
  saveKey: (key: string) => void
}

export function ImageGenerationPanel({
  settings,
  configuredSecrets,
  saving,
  isAdminOnly,
  canManage = true,
  company,
  updateSetting,
  saveKey,
}: IProps): React.JSX.Element {
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const { data: imageModelsRaw } = useQuery({
    queryKey: queryKeys.imageModels(orgId),
    queryFn: () => (company ? fetchImageModels(company) : Promise.resolve({ models: [] })),
    enabled: !!company,
  })
  const imageModels = imageModelsRaw?.models ?? []
  const refImageModels = imageModels.filter((m) => m.supportsReferenceImages)

  return (
    <ImageGenerationPanelView
      settings={settings}
      configuredSecrets={configuredSecrets}
      saving={saving}
      isAdminOnly={isAdminOnly}
      canManage={canManage}
      refImageModels={refImageModels}
      updateSetting={updateSetting}
      saveKey={saveKey}
    />
  )
}
